// Formula parser for KPI calculations
// Supports basic arithmetic operations and variable references

export class FormulaParser {
  constructor(variables = {}) {
    this.variables = variables;
  }

  // Set or update variables (like company goals)
  setVariables(variables) {
    this.variables = { ...this.variables, ...variables };
  }

  // Parse and evaluate a formula string
  evaluate(formula) {
    if (!formula || typeof formula !== 'string') {
      return null;
    }

    try {
      // Replace variable references with their values
      let processedFormula = this.replaceVariables(formula);
      
      // Clean up the formula for evaluation
      processedFormula = this.cleanFormula(processedFormula);
      
      // Evaluate the formula
      const result = this.safeEval(processedFormula);
      
      return isNaN(result) ? null : result;
    } catch (error) {
      console.error('Formula evaluation error:', error);
      return null;
    }
  }

  // Replace variable references like {company.buildRevenue} with actual values
  replaceVariables(formula) {
    let processedFormula = formula;
    
    // Match patterns like {company.buildRevenue} or {buildRevenue}
    const variablePattern = /\{([^}]+)\}/g;
    
    processedFormula = processedFormula.replace(variablePattern, (match, variablePath) => {
      const value = this.getVariableValue(variablePath);
      if (value === null) return match; // Keep original if variable not found
      
      // If the value is a string with M/K labels, extract the numeric part for calculation
      if (typeof value === 'string' && (value.includes('M') || value.includes('k') || value.includes('K'))) {
        const numericValue = this.extractNumericValue(value);
        return numericValue !== null ? numericValue : match;
      }
      
      return value;
    });
    
    return processedFormula;
  }

  // Get value from nested variable path (e.g., "company.buildRevenue")
  getVariableValue(path) {
    const keys = path.split('.');
    let value = this.variables;
    
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return null;
      }
    }
    
    // Convert string values to numbers if possible, but preserve M/K labels and comparison operators
    if (typeof value === 'string') {
      // If it contains M/K labels, preserve the original string
      if (value.includes('M') || value.includes('k') || value.includes('K')) {
        return value;
      }
      
      // If it contains comparison operators (>, <, >=, <=), preserve the original string
      if (value.match(/^[><=]+/)) {
        return value;
      }
      
      // Remove currency symbols, commas, and percentage signs
      const cleanValue = value.replace(/[$,%]/g, '');
      const numValue = parseFloat(cleanValue);
      return isNaN(numValue) ? value : numValue;
    }
    
    return typeof value === 'number' ? value : null;
  }

  // Extract numeric value from string with M/K labels
  extractNumericValue(value) {
    if (typeof value !== 'string') return value;
    
    // Handle M (millions)
    if (value.includes('M')) {
      const match = value.match(/(\d+(?:\.\d+)?)M/);
      if (match) {
        return parseFloat(match[1]) * 1000000;
      }
    }
    
    // Handle K/k (thousands)
    if (value.includes('K') || value.includes('k')) {
      const match = value.match(/(\d+(?:\.\d+)?)[Kk]/);
      if (match) {
        return parseFloat(match[1]) * 1000;
      }
    }
    
    // If no M/K labels, try to parse as regular number
    const cleanValue = value.replace(/[$,%]/g, '');
    const numValue = parseFloat(cleanValue);
    return isNaN(numValue) ? null : numValue;
  }

  // Clean formula for safe evaluation
  cleanFormula(formula) {
    // Remove any remaining non-numeric, non-operator characters except for decimal points
    return formula.replace(/[^0-9+\-*/().\s]/g, '');
  }

  // Safe evaluation of mathematical expressions
  safeEval(expression) {
    // Only allow basic arithmetic operations
    const allowedChars = /^[0-9+\-*/().\s]+$/;
    if (!allowedChars.test(expression)) {
      throw new Error('Invalid characters in formula');
    }
    
    // Use Function constructor for safer evaluation
    return new Function(`return ${expression}`)();
  }

  // Format the result based on the original formula context
  formatResult(result, originalFormula) {
    if (result === null) return null;
    
    // Check if any variables in the original formula had comparison operators
    const hasComparisonOperators = originalFormula.match(/\{[^}]+\}/g)?.some(variable => {
      const cleanVariable = variable.replace(/[{}]/g, '');
      const value = this.getVariableValue(cleanVariable);
      return typeof value === 'string' && value.match(/^[><=]+/);
    });
    
    // If variables had comparison operators, preserve the original variable value
    if (hasComparisonOperators) {
      const variableMatch = originalFormula.match(/\{[^}]+\}/);
      if (variableMatch) {
        const cleanVariable = variableMatch[0].replace(/[{}]/g, '');
        const value = this.getVariableValue(cleanVariable);
        return value; // Return the original value with comparison operator
      }
    }
    
    // Check if the original formula had currency formatting
    if (originalFormula.includes('$')) {
      return `$${this.formatNumber(result)}`;
    }
    
    // Check if the original formula had percentage formatting
    if (originalFormula.includes('%')) {
      return `${result}%`;
    }
    
    // Check if any variables in the original formula had M/K labels
    const hasMKLabels = originalFormula.match(/\{[^}]+\}/g)?.some(variable => {
      const cleanVariable = variable.replace(/[{}]/g, '');
      const value = this.getVariableValue(cleanVariable);
      return typeof value === 'string' && (value.includes('M') || value.includes('k') || value.includes('K'));
    });
    
    // If variables had M/K labels, preserve that formatting
    if (hasMKLabels) {
      return this.formatNumber(result);
    }
    
    // Default number formatting
    return this.formatNumber(result);
  }

  // Format number with appropriate precision
  formatNumber(num) {
    if (num >= 1000000) {
      const millions = num / 1000000;
      // Only show decimal if it's not .0
      return (millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1)) + 'M';
    } else if (num >= 1000) {
      const thousands = num / 1000;
      // Only show decimal if it's not .0
      return (thousands % 1 === 0 ? thousands.toFixed(0) : thousands.toFixed(1)) + 'k';
    } else {
      return num.toFixed(0);
    }
  }

  // Validate formula syntax
  validateFormula(formula) {
    if (!formula) return { isValid: false, error: 'Formula is empty' };
    
    try {
      // Test variable replacement
      const processedFormula = this.replaceVariables(formula);
      
      // Test if it can be cleaned and evaluated
      const cleanedFormula = this.cleanFormula(processedFormula);
      
      // Try to evaluate with test values
      const testResult = this.safeEval(cleanedFormula);
      
      if (isNaN(testResult)) {
        return { isValid: false, error: 'Invalid mathematical expression' };
      }
      
      return { isValid: true, error: null };
    } catch (error) {
      return { isValid: false, error: error.message };
    }
  }

  // Get available variables for formula suggestions
  getAvailableVariables() {
    const variables = [];
    
    const addVariables = (obj, prefix = '') => {
      for (const [key, value] of Object.entries(obj)) {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        if (typeof value === 'object' && value !== null) {
          addVariables(value, fullKey);
        } else {
          variables.push({
            key: fullKey,
            value: value,
            display: `{${fullKey}}`
          });
        }
      }
    };
    
    addVariables(this.variables);
    return variables;
  }
}

// Predefined formula templates
export const formulaTemplates = [
  {
    name: 'Quarterly Revenue Goal',
    description: '1/4 of annual revenue goal',
    formula: '{company.buildRevenue} / 4',
    category: 'Revenue'
  },
  {
    name: 'Monthly Revenue Goal',
    description: '1/12 of annual revenue goal',
    formula: '{company.buildRevenue} / 12',
    category: 'Revenue'
  },
  {
    name: 'Sales Commission (10%)',
    description: '10% of revenue generated',
    formula: '{revenue} * 0.1',
    category: 'Compensation'
  },
  {
    name: 'Profit Margin Target',
    description: '35% of revenue',
    formula: '{revenue} * 0.35',
    category: 'Profit'
  },
  {
    name: 'Lead Conversion Rate',
    description: 'Leads converted to sales',
    formula: '({convertedLeads} / {totalLeads}) * 100',
    category: 'Sales'
  }
];

// Helper function to create a formula parser with company goals and custom variables
export const createKPIParser = (companyGoals = [], customVariables = []) => {
  const variables = {
    company: {},
    custom: {}
  };
  
  // Convert company goals to variables
  companyGoals.forEach(goal => {
    const key = goal.label.toLowerCase().replace(/\s+/g, '');
    variables.company[key] = goal.value;
  });
  
  // Convert custom variables to variables
  customVariables.forEach(variable => {
    const key = variable.name.toLowerCase().replace(/\s+/g, '');
    variables.custom[key] = variable.value;
  });
  
  return new FormulaParser(variables);
}; 